import { Fragment, useMemo } from 'react'
import { Logo } from '@/common/assets/svg'
import { Button } from '@/common/ui'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/common/ui/dialog'
import { AccountType } from '@/common/types'
import { ApplicationFlowShell } from './components/ApplicationFlowShell'
import { ApplicationFlowActionFooter } from './components/ApplicationFlowActionFooter'
import { AdviserFlowProgressBar } from './components/AdviserFlowProgressBar'
import {
    APPLICATION_FLOW_VIEW_IDS,
    APPLICATION_FLOW_STEP_IDS,
    type ApplicationFlowViewEntry,
} from './applicationFlowConfig'
import { AddAccountTypeSelectionStep } from './features/add-account/components/AddAccountTypeSelectionStep'
import { AddAccountParticipantDetailsStep } from './features/add-account/components/AddAccountParticipantDetailsStep'
import { AddAccountParticipantCommsStep } from './features/add-account/components/AddAccountParticipantCommsStep'
import { AddAccountParticipantSurveyStep } from './features/add-account/components/AddAccountParticipantSurveyStep'
import { AddAccountParticipantAccessReviewStep } from './features/add-account/components/AddAccountParticipantAccessReviewStep'
import { AddAccountTermsAndConditionsStep } from './features/add-account/components/AddAccountTermsAndConditionsStep'
import { useAddAccountLogic } from './features/add-account/useAddAccountLogic'
import { normalizeToAddressDetails } from '@/common/utils/utils'
import { type AccountParticipantAccessProfile } from '@/account/type'
import type { SurveyFormValues } from '@/user/survey/surveyTypes'

type Props = {
    open: boolean
    onClose: () => void
    initialAccountType?: AccountType
}

export default function AddAccountModal(props: Props) {
    const { open, onClose, initialAccountType } = props
    const {
        handleResolvedAction,
        isTrust,
        primaryParticipant,
        primaryAccountRegistrationAddress,
        progressPercentage,
        resolver: { step, visibleViews, primaryAction, secondaryAction, tertiaryAction },
        isSubmitting,
        selectedAccountType,
        setCurrentStepId,
        setIsTrust,
        setSelectedAccountType,
        supportedAccountTypes,
        updateParticipant,
        applyParticipantSurvey,
        registerStepSubmitHandler,
        isPrimaryAdviserUser,
    } = useAddAccountLogic({
        open,
        onClose,
        initialAccountType,
    })

    const viewRenderers = useMemo(
        () => ({
            [APPLICATION_FLOW_VIEW_IDS.SHARED_ACCOUNT_TYPE]: (viewEntry: ApplicationFlowViewEntry) => (
                <AddAccountTypeSelectionStep
                    selectedAccountType={selectedAccountType ?? null}
                    setSelectedAccountType={(accountType) => setSelectedAccountType(accountType ?? undefined)}
                    isTrust={isTrust}
                    setIsTrust={setIsTrust}
                    supportedAccountTypes={viewEntry.supportedAccountTypes ?? supportedAccountTypes}
                    isAdviser={isPrimaryAdviserUser}
                />
            ),

            [APPLICATION_FLOW_VIEW_IDS.SHARED_PARTICIPANT_DETAILS]: () => (
                <AddAccountParticipantDetailsStep
                    participant={primaryParticipant}
                    onParticipantChange={(updates) => updateParticipant(0, updates)}
                />
            ),
            [APPLICATION_FLOW_VIEW_IDS.ADVISER_PARTICIPANT_COMMS]: () => {
                const clientPostalAddress = primaryParticipant.differentPostalAddress
                    ? primaryParticipant.postal
                    : primaryParticipant.residential

                if (!clientPostalAddress) {
                    Promise.resolve().then(() => setCurrentStepId(APPLICATION_FLOW_STEP_IDS.PARTICIPANT_DETAILS))
                    return null
                }

                return (
                    <AddAccountParticipantCommsStep
                        clientPostalAddress={clientPostalAddress}
                        adviserRegistrationAddress={normalizeToAddressDetails(primaryAccountRegistrationAddress)}
                        defaultAddressType={primaryParticipant.tradeRegistryAddressType}
                        onAddressTypeChange={(addressType) =>
                            updateParticipant(0, { tradeRegistryAddressType: addressType })
                        }
                    />
                )
            },
            [APPLICATION_FLOW_VIEW_IDS.ADVISER_PARTICIPANT_SURVEY]: () => (
                <AddAccountParticipantSurveyStep
                    participant={primaryParticipant}
                    onSubmit={(survey: SurveyFormValues) => applyParticipantSurvey(0, survey)}
                    registerStepSubmitHandler={registerStepSubmitHandler}
                />
            ),
            [APPLICATION_FLOW_VIEW_IDS.SHARED_PARTICIPANT_ACCESS_REVIEW]: (viewEntry: ApplicationFlowViewEntry) => (
                <AddAccountParticipantAccessReviewStep
                    participant={primaryParticipant}
                    onAccessProfileChange={(accessProfile) => updateParticipant(0, { accessProfile })}
                    supportedAccessProfiles={
                        viewEntry.supportedAccessProfiles as AccountParticipantAccessProfile[] | undefined
                    }
                />
            ),
            [APPLICATION_FLOW_VIEW_IDS.SHARED_TERMS_AND_CONDITIONS]: () => (
                <AddAccountTermsAndConditionsStep isAdviser={isPrimaryAdviserUser} />
            ),
        }),
        [
            isPrimaryAdviserUser,
            isTrust,
            primaryParticipant,
            primaryAccountRegistrationAddress,
            selectedAccountType,
            setCurrentStepId,
            setIsTrust,
            setSelectedAccountType,
            supportedAccountTypes,
            applyParticipantSurvey,
            registerStepSubmitHandler,
            updateParticipant,
        ],
    )

    return (
        <>
            <Dialog open={open}>
                <DialogContent className="m-0 h-full w-full rounded-none grid-rows-[auto_1fr] grid gap-6 top-0 translate-y-0 pb-24 sm:pb-0 p-6 lg:p-6">
                    <DialogHeader>
                        <div className="self-stretch w-full max-w-[600px] mx-auto flex justify-between items-center">
                            <DialogTitle className="sr-only">{step.ui.title}</DialogTitle>
                            <Logo width={128} height={40} />
                            {tertiaryAction && (
                                <Button
                                    variant="ghost"
                                    disabled={isSubmitting}
                                    onClick={() => handleResolvedAction(tertiaryAction)}
                                    data-icon-left="false"
                                    data-icon-right="false"
                                    data-show-badge="false"
                                    data-show-text="true"
                                    data-size="default"
                                    data-state="default"
                                    data-type="ghost"
                                    className="h-14 py-2.5 bg-white/0 rounded-[100px] flex justify-center items-center gap-2.5 border-0"
                                >
                                    <span className="justify-start text-black text-base font-medium font-['Inter'] leading-6">
                                        {tertiaryAction.label}
                                    </span>
                                </Button>
                            )}
                        </div>
                    </DialogHeader>
                    <div className="w-full flex flex-col justify-start items-center min-h-0">
                        <ApplicationFlowShell
                            progress={<AdviserFlowProgressBar percentage={progressPercentage} />}
                            outerClassName="justify-start"
                            scrollClassName="justify-start"
                            contentClassName="mb-[100px] md:mb-0"
                            footer={
                                primaryAction || secondaryAction ? (
                                    <ApplicationFlowActionFooter
                                        primaryAction={primaryAction}
                                        secondaryAction={secondaryAction}
                                        disabled={isSubmitting}
                                        onAction={handleResolvedAction}
                                    />
                                ) : null
                            }
                        >
                            <div className="w-full flex flex-col gap-6">
                                {visibleViews.map((viewEntry, index) => {
                                    const renderView = viewRenderers[viewEntry.view]
                                    if (!renderView) return null

                                    return (
                                        <Fragment key={`${viewEntry.view}-${index}`}>{renderView(viewEntry)}</Fragment>
                                    )
                                })}
                            </div>
                        </ApplicationFlowShell>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    )
}
